import React from 'react';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ExternalLink, Github, Star, Code, Zap, Users, Trophy, Calendar } from "lucide-react";

const Portfolio: React.FC = () => {
  const projects = [
    {
      id: 1,
      title: "AI Code Assistant Platform",
      description: "Full-stack web application that helps developers write better code using AI-powered suggestions and real-time collaboration features.",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop",
      technologies: ["React", "TypeScript", "Node.js", "OpenAI API", "PostgreSQL"],
      githubUrl: "https://github.com/example/ai-code-assistant",
      liveUrl: "https://ai-code-assistant.demo.com",
      stars: 234,
      category: "Web Development"
    },
    {
      id: 2,
      title: "Vibe Community Hub",
      description: "Modern community platform for developers to share resources, collaborate on projects, and learn together with gamification elements.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      technologies: ["Next.js", "Prisma", "Tailwind CSS", "Socket.io", "Redis"],
      githubUrl: "https://github.com/example/vibe-community",
      liveUrl: "https://vibe-community.demo.com",
      stars: 189,
      category: "Community Platform"
    },
    {
      id: 3,
      title: "Smart Code Reviewer",
      description: "Automated code review tool that analyzes code quality, suggests improvements, and integrates seamlessly with popular version control systems.",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop",
      technologies: ["Python", "FastAPI", "Docker", "GitHub API", "Machine Learning"],
      githubUrl: "https://github.com/example/smart-code-reviewer",
      liveUrl: "https://smart-reviewer.demo.com",
      stars: 156,
      category: "Developer Tools"
    },
    {
      id: 4,
      title: "Responsive Design System",
      description: "Comprehensive design system and component library built with accessibility-first principles and modern design patterns.",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=400&fit=crop",
      technologies: ["React", "Storybook", "Styled Components", "TypeScript", "Jest"],
      githubUrl: "https://github.com/example/design-system",
      liveUrl: "https://design-system.demo.com",
      stars: 298,
      category: "UI/UX"
    },
    {
      id: 5,
      title: "Real-time Collaboration IDE",
      description: "Browser-based IDE with real-time collaboration features, integrated terminal, and support for multiple programming languages.",
      image: "https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=600&h=400&fit=crop",
      technologies: ["Vue.js", "WebRTC", "Monaco Editor", "WebSockets", "Docker"],
      githubUrl: "https://github.com/example/collab-ide",
      liveUrl: "https://collab-ide.demo.com",
      stars: 412,
      category: "Development Environment"
    },
    {
      id: 6,
      title: "API Performance Monitor",
      description: "Comprehensive monitoring solution for REST APIs with real-time analytics, alerting system, and performance optimization suggestions.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      technologies: ["Go", "InfluxDB", "Grafana", "Kubernetes", "Prometheus"],
      githubUrl: "https://github.com/example/api-monitor",
      liveUrl: "https://api-monitor.demo.com",
      stars: 167,
      category: "DevOps"
    }
  ];

  const achievements = [
    {
      icon: Trophy,
      title: "Open Source Contributor",
      description: "500+ contributions to popular open source projects",
      count: "500+"
    },
    {
      icon: Star,
      title: "GitHub Stars",
      description: "Total stars across all repositories",
      count: "1.2K+"
    },
    {
      icon: Users,
      title: "Community Members",
      description: "Active developers in our community",
      count: "3.5K+"
    },
    {
      icon: Code,
      title: "Projects Completed",
      description: "Successfully delivered projects",
      count: "50+"
    }
  ];

  const categories = ["All", "Web Development", "Community Platform", "Developer Tools", "UI/UX", "Development Environment", "DevOps"];
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredProjects = selectedCategory === "All" 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-br from-purple-50 via-white to-cyan-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 bg-clip-text text-transparent mb-6">
            Portfolio Showcase
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our collection of innovative projects, tools, and contributions to the developer community. 
            Each project represents our commitment to quality, innovation, and collaborative development.
          </p>
        </div>

        {/* Achievements */}
        <div className="mb-16">
          <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {achievements.map((achievement, index) => (
                <Card key={index} className="text-center p-6 hover:shadow-lg transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-0">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500">
                        <achievement.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{achievement.count}</div>
                    <div className="text-sm font-semibold text-gray-700 mb-1">{achievement.title}</div>
                    <div className="text-xs text-gray-500">{achievement.description}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  "px-6 py-2 rounded-full transition-all duration-300",
                  selectedCategory === category
                    ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white shadow-lg"
                    : "border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-600"
                )}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="space-y-8 max-w-5xl mx-auto">
          {filteredProjects.map((project, index) => (
            <Card key={project.id} className={cn(
              "overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 bg-white/90 backdrop-blur-sm",
              "transform hover:-translate-y-2"
            )}>
              <div className={cn(
                "flex flex-col gap-8 p-8",
                index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
              )}>
                {/* Project Image */}
                <div className="lg:w-1/2">
                  <div className="relative group overflow-hidden rounded-xl">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex gap-3">
                        <Button size="sm" className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Live Demo
                        </Button>
                        <Button size="sm" variant="outline" className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
                          <Github className="w-4 h-4 mr-2" />
                          Code
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="lg:w-1/2 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 text-sm font-medium bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 rounded-full">
                      {project.category}
                    </span>
                    <div className="flex items-center gap-1 text-gray-500">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{project.stars}</span>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{project.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>

                  {/* Technologies */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <Button className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:from-purple-700 hover:to-cyan-700">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live
                    </Button>
                    <Button variant="outline" className="border-gray-300 text-gray-700 hover:border-purple-400 hover:text-purple-600">
                      <Github className="w-4 h-4 mr-2" />
                      Source Code
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <Card className="max-w-2xl mx-auto p-8 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 border-0 text-white">
            <CardContent className="p-0">
              <Zap className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">Ready to Collaborate?</h3>
              <p className="text-purple-100 mb-6">
                Have an exciting project in mind? Let's work together to bring your ideas to life with cutting-edge technology and innovative solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  Start a Project
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View All Projects
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;